import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { shouldAutoplayVideo } from "@/lib/autoplay";
import { Play, X, SpeakerSimpleHigh, ArrowRight } from "@phosphor-icons/react";

const CARDS = [
  { key: "tour", title: "Watch a full month-end", subtitle: "Product tour" },
  { key: "why", title: "Why accountants choose Lottie", subtitle: "60-second explainer" },
  // Definition walkthroughs — pull marketing visitors straight into the guides.
  { guide: "what-is-a-prepayment", title: "What is a prepayment?", subtitle: "Definition walkthrough", href: "/guides/what-is-a-prepayment" },
  { guide: "what-is-deferred-income", title: "What is deferred income?", subtitle: "Definition walkthrough", href: "/guides/what-is-deferred-income" },
];

// Resolve the media endpoints + status check for either a product clip (tour/why)
// or a public guide walkthrough (/api/guides/{slug}/walkthrough).
function cardEndpoints(card) {
  if (card.guide) {
    const base = `${API}/guides/${card.guide}/walkthrough`;
    return { statusUrl: base, video: `${base}/video`, thumbnail: `${base}/thumbnail`, isAvailable: (d) => !!d?.available };
  }
  const base = `${API}/${card.key}`;
  return { statusUrl: `${base}/status`, video: `${base}/video`, thumbnail: `${base}/thumbnail`, isAvailable: (d) => !!d?.has_video };
}

function HoverVideoCard({ card, onOpen, autoPreview = false }) {
  const [ready, setReady] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const vidRef = useRef(null);
  const ep = cardEndpoints(card);

  useEffect(() => {
    fetch(ep.statusUrl)
      .then((r) => r.json())
      .then((d) => setReady(ep.isAvailable(d)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ep.statusUrl]);

  // Gently auto-preview the featured clip on load so motion catches the eye.
  useEffect(() => {
    if (!ready || !autoPreview || !shouldAutoplayVideo()) return;
    const v = vidRef.current;
    if (!v) return;
    v.muted = true; v.defaultMuted = true;
    const t = setTimeout(() => {
      v.play?.().then(() => setAutoOn(true)).catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [ready, autoPreview]);

  const enter = () => {
    setHovering(true);
    const v = vidRef.current;
    if (!v || !shouldAutoplayVideo()) return;
    v.muted = true; v.defaultMuted = true;
    if (!autoOn) { try { v.currentTime = 0; } catch { /* noop */ } }
    v.play?.().catch(() => {});
  };
  const leave = () => {
    setHovering(false);
    if (autoPreview) return; // keep the featured clip gently looping
    const v = vidRef.current;
    if (v) { v.pause?.(); try { v.currentTime = 0; } catch { /* noop */ } }
  };

  if (!ready) return null;
  const showVideo = hovering || autoOn;

  return (
    <button
      type="button"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onClick={() => onOpen(card)}
      data-testid={`video-card-${card.key || card.guide}`}
      className="group relative text-left rounded-3xl overflow-hidden border border-slate-200 bg-navy shadow-card hover:shadow-card-hover transition-all hover:-translate-y-0.5"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={ep.thumbnail}
          alt={card.title}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showVideo ? "opacity-0" : "opacity-100"}`}
          loading="lazy"
        />
        <video
          ref={vidRef}
          muted
          loop
          playsInline
          preload={autoPreview ? "auto" : "none"}
          poster={ep.thumbnail}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showVideo ? "opacity-100" : "opacity-0"}`}
        >
          <source src={ep.video} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[0.62rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full text-white shadow" style={{ background: "rgba(243,122,36,0.95)" }}>
          Narrated by Lottie
        </span>
        <span className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${autoOn && !hovering ? "opacity-70" : "opacity-100"}`}>
          <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(243,122,36,0.95)" }}>
            <Play size={26} weight="fill" className="text-white ml-1" />
          </span>
        </span>
      </div>
      <div className="p-4">
        <p className="font-heading font-bold text-white text-base leading-tight">{card.title}</p>
        <p className="text-[0.8rem] text-white/60 mt-1 inline-flex items-center gap-1.5">
          <SpeakerSimpleHigh size={13} weight="fill" /> {card.subtitle} · tap to watch with sound
        </p>
      </div>
    </button>
  );
}

export default function MarketingVideoCards({ autoPreviewFirst = false }) {
  const [open, setOpen] = useState(null);

  return (
    <section className="max-w-5xl mx-auto px-6 py-16" data-testid="marketing-video-cards">
      <div className="overline mb-3" style={{ color: "#F37A24" }}>See Lottie moving</div>
      <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl mb-2">Watch it in action</h2>
      <p className="text-slate-600 mb-8 max-w-2xl">Hover any clip to preview it — tap to watch the full narrated walkthrough with sound.</p>
      <div className="grid gap-6 sm:grid-cols-2">
        {CARDS.map((c, i) => <HoverVideoCard key={c.key || c.guide} card={c} onOpen={setOpen} autoPreview={autoPreviewFirst && i === 0} />)}
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="video-lightbox" onClick={() => setOpen(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(null)} data-testid="video-lightbox-close"
              className="absolute -top-11 right-0 text-white/80 hover:text-white inline-flex items-center gap-1.5 text-sm font-semibold">
              <X size={20} weight="bold" /> Close
            </button>
            <video controls autoPlay playsInline className="w-full rounded-2xl bg-black shadow-2xl"
              poster={cardEndpoints(open).thumbnail}>
              <source src={cardEndpoints(open).video} type="video/mp4" />
            </video>
            {open.href && (
              <Link to={open.href} data-testid="video-lightbox-guide-link"
                className="mt-4 inline-flex items-center gap-1.5 text-white font-semibold text-sm hover:gap-2.5 transition-all">
                Read the full guide <ArrowRight size={16} weight="bold" />
              </Link>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
